import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import Calculator from '../components/Calculator';
import {
  getNombreMes,
  calcularCuotaMensual,
  generarCronogramaAmortizacion,
  calcularPagosRealizados,
  esPrestamoActivo
} from '../utils/amortizacion';

const ValoresManagement = () => {
  const { hasPermission, user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [socios, setSocios] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  // Se puede llegar con ?socio=<id>&mes=<m>&año=<a> (p. ej. desde "Para
  // regularizar" en el Reporte de Deudas) para abrir directo ese registro
  const [abrirDesdeUrl] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const socio = q.get('socio');
    const mes = parseInt(q.get('mes'), 10);
    const año = parseInt(q.get('año'), 10);
    return socio && mes >= 1 && mes <= 12 && año > 2000 ? { socio, mes, año } : null;
  });
  const [filterMes, setFilterMes] = useState(abrirDesdeUrl?.mes || new Date().getMonth() + 1);
  const [filterAño, setFilterAño] = useState(abrirDesdeUrl?.año || new Date().getFullYear());
  const [registrosDe, setRegistrosDe] = useState(null); // 'año-mes' de los registros cargados
  const [pendienteAbrir, setPendienteAbrir] = useState(abrirDesdeUrl);
  const [refreshPrestamos, setRefreshPrestamos] = useState(0);
  const [hasPrestamo, setHasPrestamo] = useState(false); // ¿Hay préstamo activo en este mes?

  // Multas pendientes del socio seleccionado (conectado con /api/multas)
  const [multasSocio, setMultasSocio] = useState([]);
  const [loadingMultas, setLoadingMultas] = useState(false);
  const [multasSeleccionadas, setMultasSeleccionadas] = useState([]);
  const [procesandoPagoMultas, setProcesandoPagoMultas] = useState(false);

  // Meses de aporte atrasados y a cuáles se aplica el aporte de este registro
  const [aportesPendientes, setAportesPendientes] = useState([]);
  const [cuotaAporte, setCuotaAporte] = useState(10);
  const [aporteExento, setAporteExento] = useState(false);
  const [cubreSeleccion, setCubreSeleccion] = useState({}); // 'año-mes' -> monto
  const [cubreEditado, setCubreEditado] = useState(false);

  const [formData, setFormData] = useState({
    mes: new Date().getMonth() + 1,
    año: new Date().getFullYear(),
    aportes: 0,
    capitalPrestamo: 0,
    interesPrestamo: 0,
    paseo: 0,
    programa: 0,
    multas: 0,
    observaciones: ''
  });

  const fetchSocios = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRegistros = useCallback(async () => {
    try {
      const response = await axios.get(`/api/registros-mensuales?mes=${filterMes}&año=${filterAño}`);
      setRegistros(response.data.data || []);
      setRegistrosDe(`${filterAño}-${filterMes}`);
    } catch (error) {
      console.error('Error fetching registros:', error);
    }
  }, [filterMes, filterAño]);

  useEffect(() => {
    fetchSocios();
  }, [fetchSocios]);

  // Abrir el registro pedido por URL cuando ya están cargados socios y registros de ese mes
  useEffect(() => {
    if (!pendienteAbrir || socios.length === 0) return;
    if (registrosDe !== `${pendienteAbrir.año}-${pendienteAbrir.mes}`) return;
    const socio = socios.find(s => s._id === pendienteAbrir.socio);
    setPendienteAbrir(null);
    window.history.replaceState(null, '', window.location.pathname);
    if (socio) handleManageSocio(socio);
    else toast.error('No se encontró el socio indicado');
  }, [pendienteAbrir, socios, registrosDe]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRegistros();
  }, [filterMes, filterAño, fetchRegistros]);

  // Verificar préstamo cuando cambia el mes/año en el formulario
  useEffect(() => {
    if (showModal && selectedSocio) {
      const checkPrestamo = async () => {
        await verificarPrestamoEnMes(selectedSocio._id, formData.mes, formData.año);
      };
      checkPrestamo();
    }
  }, [formData.mes, formData.año, showModal, selectedSocio]);

  // Meses de aporte que el socio debe ANTES del mes que se registra
  useEffect(() => {
    if (!showModal || !selectedSocio) return;
    const excluir = formData._id ? `&excluirRegistro=${formData._id}` : '';
    axios.get(`/api/reportes-prestamos/deudas/aportes-pendientes/${selectedSocio._id}?mes=${formData.mes}&año=${formData.año}${excluir}`)
      .then(res => {
        setAportesPendientes(res.data.pendientes || []);
        setCuotaAporte(res.data.cuota ?? 10);
        setAporteExento(!!res.data.exento);
      })
      .catch(() => { setAportesPendientes([]); });
  }, [showModal, selectedSocio, formData.mes, formData.año, formData._id]);

  // Sugerencia automática: lo aportado por encima de la cuota cubre primero
  // los meses atrasados más antiguos (mientras no se haya editado a mano)
  useEffect(() => {
    if (cubreEditado || aportesPendientes.length === 0) return;
    let sobrante = (parseFloat(formData.aportes) || 0) - cuotaAporte;
    const sugerida = {};
    for (const p of aportesPendientes) {
      if (sobrante <= 0.001) break;
      const monto = Math.min(p.falta, sobrante);
      sugerida[`${p.año}-${p.mes}`] = Math.round(monto * 100) / 100;
      sobrante -= monto;
    }
    setCubreSeleccion(sugerida);
  }, [formData.aportes, aportesPendientes, cuotaAporte, cubreEditado]);

  const alternarMesCubierto = (p) => {
    const k = `${p.año}-${p.mes}`;
    setCubreEditado(true);
    setCubreSeleccion(prev => {
      const nuevo = { ...prev };
      if (nuevo[k]) delete nuevo[k]; else nuevo[k] = p.falta;
      return nuevo;
    });
  };

  const totalCubreAnteriores = Object.values(cubreSeleccion).reduce((a, v) => a + (v || 0), 0);

  const handleManageSocio = async (socio) => {
    setSelectedSocio(socio);
    
    // Buscar si ya existe registro para este mes
    const registroExistente = registros.find(r => r.socio._id === socio._id);
    
    let mesParaVerificar = filterMes;
    let añoParaVerificar = filterAño;
    
    if (registroExistente) {
      setFormData({
        mes: registroExistente.mes,
        año: registroExistente.año,
        aportes: registroExistente.aportes,
        capitalPrestamo: registroExistente.capitalPrestamo || 0,
        interesPrestamo: registroExistente.interesPrestamo || 0,
        paseo: registroExistente.paseo,
        programa: registroExistente.programa || 0,
        multas: registroExistente.multas || 0,
        observaciones: registroExistente.observaciones,
        _id: registroExistente._id
      });
      const existentes = {};
      (registroExistente.aportesCubre || []).forEach(c => { existentes[`${c.año}-${c.mes}`] = c.monto; });
      setCubreSeleccion(existentes);
      setCubreEditado((registroExistente.aportesCubre || []).length > 0);
      mesParaVerificar = registroExistente.mes;
      añoParaVerificar = registroExistente.año;
    } else {
      setFormData({
        mes: filterMes,
        año: filterAño,
        aportes: 0,
        capitalPrestamo: 0,
        interesPrestamo: 0,
        paseo: 0,
        programa: 0,
        multas: 0,
        observaciones: ''
      });
      setCubreSeleccion({});
      setCubreEditado(false);
    }
    
    // Verificar si hay préstamo activo EN EL MES DEL FORMULARIO
    await verificarPrestamoEnMes(socio._id, mesParaVerificar, añoParaVerificar);

    // Incrementar el trigger para recargar datos de préstamos cuando abre el modal
    setRefreshPrestamos(prev => prev + 1);
    setMultasSeleccionadas([]);
    await fetchMultasSocio(socio._id);
    setShowModal(true);
  };

  const fetchMultasSocio = async (socioId) => {
    try {
      setLoadingMultas(true);
      const response = await axios.get(`/api/multas/socio/${socioId}`);
      setMultasSocio(response.data.data || []);
    } catch (error) {
      console.error('Error al obtener multas del socio:', error);
      setMultasSocio([]);
    } finally {
      setLoadingMultas(false);
    }
  };

  const toggleMultaSeleccionada = (multaId) => {
    setMultasSeleccionadas(prev =>
      prev.includes(multaId) ? prev.filter(id => id !== multaId) : [...prev, multaId]
    );
  };

  const pagarMultasSeleccionadas = async () => {
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para registrar pagos de multas');
      return;
    }
    if (multasSeleccionadas.length === 0) {
      toast.error('Selecciona al menos una multa');
      return;
    }

    setProcesandoPagoMultas(true);
    try {
      const multasAPagar = multasSocio.filter(m => multasSeleccionadas.includes(m._id));
      const totalPagado = multasAPagar.reduce((sum, m) => sum + (m.monto || 0), 0);

      // El backend marca cada multa como pagada y la suma en el registro de
      // este mes (uno por uno para no pisar el total entre llamadas)
      for (const m of multasAPagar) {
        await axios.patch(`/api/multas/${m._id}/pagar`, { mes: formData.mes, año: formData.año });
      }

      // Traer el registro del mes ya actualizado (pudo haberse creado recién)
      const resReg = await axios.get(`/api/registros-mensuales?socio=${selectedSocio._id}&mes=${formData.mes}&año=${formData.año}`);
      const registroDelMes = (resReg.data.data || [])[0];
      if (registroDelMes) {
        setFormData(prev => ({ ...prev, multas: registroDelMes.multas || 0, _id: prev._id || registroDelMes._id }));
      }
      setMultasSeleccionadas([]);
      toast.success(`${multasAPagar.length} multa(s) marcada(s) como pagada(s): ${formatCurrency(totalPagado)}`);

      await fetchMultasSocio(selectedSocio._id);
      await fetchRegistros();
    } catch (error) {
      console.error('Error al pagar multas:', error);
      toast.error(error.response?.data?.message || 'Error al registrar el pago de las multas');
    } finally {
      setProcesandoPagoMultas(false);
    }
  };

  const verificarPrestamoEnMes = async (socioId, mes, año) => {
    try {
      const prestamos = await obtenerPrestamosActivos(socioId);
      
      console.log('📋 Verificando préstamo para:', { socioId, mes, año });
      console.log('📋 Préstamos encontrados:', prestamos);
      
      // Verificar si hay un préstamo que esté activo en este mes/año
      const prestamoEnMes = prestamos.some(p => {
        const fechaSolicitud = new Date(p.fechaSolicitud);
        const mesSolicitud = fechaSolicitud.getMonth() + 1;
        const añoSolicitud = fechaSolicitud.getFullYear();
        const plazo = parseInt(p.plazo) || parseInt(p.cantidadMeses) || 0;
        
        // Calcular mes mínimo (inicio del préstamo)
        // Si el mes está ANTES del inicio, está bloqueado
        // Si está DESPUÉS o DENTRO, está permitido (puede haberse extendido)
        const estáAntesDeLaFecha = (
          año < añoSolicitud || 
          (año === añoSolicitud && mes < mesSolicitud)
        );
        
        console.log('📋 Préstamo:', {
          fechaSolicitud: p.fechaSolicitud,
          mesSolicitud,
          añoSolicitud,
          plazo,
          mesVerificacion: mes,
          añoVerificacion: año,
          estáAntesDeLaFecha,
          resultado: !estáAntesDeLaFecha
        });
        
        // Si está DESPUÉS del inicio (o en el mismo mes de inicio), permite escribir
        return !estáAntesDeLaFecha;
      });
      
      console.log('📋 Resultado: ¿Hay préstamo activo?', prestamoEnMes);
      setHasPrestamo(prestamoEnMes);
    } catch (error) {
      console.error('Error al verificar préstamo:', error);
      setHasPrestamo(false);
    }
  };

  const obtenerPrestamosActivos = async (socioId) => {
    try {
      // No filtrar por un solo estado en el backend (solo soporta match exacto):
      // un préstamo activo puede estar en 'aprobada' O 'desembolsado' (p. ej.
      // dinero ya entregado). Antes esto solo buscaba 'aprobada' y se perdían
      // los préstamos ya desembolsados, como el de Ana Matailo.
      const response = await axios.get(`/api/solicitudes-prestamo?socio=${socioId}`);
      const todas = response.data.data || [];
      return todas.filter(p => esPrestamoActivo(p.estado));
    } catch (error) {
      console.error('Error al obtener préstamos:', error);
      return [];
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSocio(null);
    setFormData({
      mes: new Date().getMonth() + 1,
      año: new Date().getFullYear(),
      aportes: 0,
      capitalPrestamo: 0,
      interesPrestamo: 0,
      paseo: 0,
      programa: 0,
      multas: 0,
      observaciones: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'observaciones') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      // Permitir valores vacíos temporalmente para que el usuario pueda escribir decimales
      const numValue = value === '' ? '' : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numValue === '' ? '' : (isNaN(numValue) ? 0 : numValue)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasPermission('socios', 'update')) {
      toast.error('No tienes permisos para registrar valores');
      return;
    }

    if (totalCubreAnteriores > (parseFloat(formData.aportes) || 0) + 0.001) {
      toast.error(`Marcaste ${formatCurrency(totalCubreAnteriores)} para meses anteriores, pero el aporte de este mes es ${formatCurrency(parseFloat(formData.aportes) || 0)}`);
      return;
    }

    // Validar que se registren los intereses acumulados
    const prestamosActivos = await obtenerPrestamosActivos(selectedSocio._id);
    if (prestamosActivos.length > 0) {
      const prestamo = prestamosActivos[0];
      const detalles = calcularCuotaMensual(prestamo);
      const interesAcumulado = detalles?.interesAlMes || 0;
      
      if (formData.interesPrestamo < interesAcumulado) {
        toast.warning(
          `⚠️ Advertencia: El interés acumulado para este mes es ${formatCurrency(interesAcumulado)}. ` +
          `Estás registrando solo ${formatCurrency(formData.interesPrestamo)}. ` +
          `Los intereses se acumulan cada mes sin excepción.`
        );
      }
    }

    try {
      // Convertir valores vacíos a 0
      const convertToNumber = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
      };

      const data = {
        socio: selectedSocio._id,
        mes: formData.mes,
        año: formData.año,
        aportes: convertToNumber(formData.aportes),
        capitalPrestamo: convertToNumber(formData.capitalPrestamo),
        interesPrestamo: convertToNumber(formData.interesPrestamo),
        paseo: convertToNumber(formData.paseo),
        programa: convertToNumber(formData.programa),
        multas: convertToNumber(formData.multas),
        aportesCubre: Object.entries(cubreSeleccion)
          .filter(([, monto]) => monto > 0)
          .map(([k, monto]) => {
            const [año, mes] = k.split('-').map(Number);
            return { mes, año, monto };
          }),
        observaciones: formData.observaciones,
        registradoPor: user.id
      };

      const response = formData._id
        ? await axios.put(`/api/registros-mensuales/${formData._id}`, data)
        : await axios.post('/api/registros-mensuales', data);
      toast.success(formData._id ? 'Registro actualizado exitosamente' : 'Registro creado exitosamente');

      // El backend finaliza solo los préstamos que quedaron en $0
      (response.data.prestamosFinalizados || []).forEach(numero => {
        toast.info(`✅ El préstamo ${numero} quedó pagado por completo y se marcó como finalizado`, { autoClose: 8000 });
      });

      handleCloseModal();
      fetchRegistros();
      // Recargar información de préstamos
      setRefreshPrestamos(prev => prev + 1);
    } catch (error) {
      console.error('Error al guardar registro:', error);
      toast.error(error.response?.data?.message || 'Error al guardar el registro');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const calcularTotalMes = (mes, año, tipo) => {
    return registros
      .filter(r => r.mes === mes && r.año === año)
      .reduce((sum, r) => {
        if (tipo === 'cuotaPrestamo') {
          // Sumar capital + interés para obtener la cuota total
          return sum + ((r.capitalPrestamo || 0) + (r.interesPrestamo || 0));
        }
        return sum + (r[tipo] || 0);
      }, 0);
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-calendar-alt me-2"></i>
                      Registro Mensual de Valores
                    </h2>
                    <p className="text-muted mb-0">
                      Registra aportes, cuotas de préstamo, paseos y programa mes a mes
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros de Mes y Año */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Mes
                  </Form.Label>
                  <Form.Select value={filterMes} onChange={(e) => setFilterMes(parseInt(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mes => (
                      <option key={mes} value={mes}>
                        {getNombreMes(mes)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Año
                  </Form.Label>
                  <Form.Select value={filterAño} onChange={(e) => setFilterAño(parseInt(e.target.value))}>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Estadísticas del Mes */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stats-card border-success">
              <Card.Body>
                <div className="stats-icon text-success">
                  <i className="fas fa-hand-holding-usd"></i>
                </div>
                <div className="stats-number text-success">
                  {formatCurrency(calcularTotalMes(filterMes, filterAño, 'aportes'))}
                </div>
                <div className="stats-label">Total Aportes</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-info">
              <Card.Body>
                <div className="stats-icon text-info">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="stats-number text-info">
                  {formatCurrency(calcularTotalMes(filterMes, filterAño, 'cuotaPrestamo'))}
                </div>
                <div className="stats-label">Total Cuotas Préstamo</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-warning">
              <Card.Body>
                <div className="stats-icon text-warning">
                  <i className="fas fa-bus"></i>
                </div>
                <div className="stats-number text-warning">
                  {formatCurrency(calcularTotalMes(filterMes, filterAño, 'paseo'))}
                </div>
                <div className="stats-label">Total Paseos</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-primary">
              <Card.Body>
                <div className="stats-icon text-primary">
                  <i className="fas fa-star"></i>
                </div>
                <div className="stats-number text-primary">
                  {formatCurrency(calcularTotalMes(filterMes, filterAño, 'programa'))}
                </div>
                <div className="stats-label">Total Programa</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Registros */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Registros de {getNombreMes(filterMes)} {filterAño}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th>Número Socio</th>
                        <th>Aportes</th>
                        <th>Cuota Préstamo</th>
                        <th>Paseo</th>
                        <th>Programa</th>
                        <th>Total</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            No hay socios registrados
                          </td>
                        </tr>
                      ) : (
                        socios.map(socio => {
                          const registro = registros.find(r => r.socio._id === socio._id);
                          const cuotaPrestamo = (registro?.capitalPrestamo || 0) + (registro?.interesPrestamo || 0);
                          const total = (registro?.aportes || 0) + cuotaPrestamo + (registro?.paseo || 0) + (registro?.programa || 0);
                          
                          return (
                            <tr key={socio._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                                  <div>
                                    <div className="fw-bold">{socio.name}</div>
                                    <small className="text-muted">{socio.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>{socio.cedula}</td>
                              <td>
                                <Badge bg="info">{socio.socioInfo?.numeroSocio || 'N/A'}</Badge>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(registro?.aportes || 0)}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-info">
                                  {formatCurrency(cuotaPrestamo)}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-warning">
                                  {formatCurrency(registro?.paseo || 0)}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-primary">
                                  {formatCurrency(registro?.programa || 0)}
                                </strong>
                              </td>
                              <td>
                                <strong className="text-primary">
                                  {formatCurrency(total)}
                                </strong>
                              </td>
                              <td className="text-center">
                                {hasPermission('socios', 'update') && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleManageSocio(socio)}
                                    title="Registrar valores"
                                  >
                                    <i className="fas fa-edit me-1"></i>
                                    Registrar
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal para registrar valores */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Registrar Valores - {selectedSocio?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="info">
              <Row>
                <Col md={6}>
                  <strong>Socio:</strong> {selectedSocio?.name}<br/>
                  <strong>Cédula:</strong> {selectedSocio?.cedula}
                </Col>
                <Col md={6}>
                  <strong>Número:</strong> {selectedSocio?.socioInfo?.numeroSocio}<br/>
                  <strong>Mes:</strong> {getNombreMes(formData.mes)} {formData.año}
                </Col>
              </Row>
            </Alert>

            {/* Información de Préstamos Activos */}
            {selectedSocio && (
              <PrestamosActivosInfo
                socioId={selectedSocio._id}
                formatCurrency={formatCurrency}
                calcularCuotaMensual={calcularCuotaMensual}
                mesActual={formData.mes}
                añoActual={formData.año}
                refreshTrigger={refreshPrestamos}
              />
            )}

            {/* Multas pendientes del socio (conectado con la pantalla de Multas) */}
            {selectedSocio && (
              <Card className="mb-3 border-danger">
                <Card.Header className="bg-danger bg-opacity-10">
                  <i className="fas fa-gavel text-danger me-2"></i>
                  <strong>Multas</strong>
                </Card.Header>
                <Card.Body>
                  {loadingMultas ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : multasSocio.filter(m => m.estado !== 'pagada').length === 0 ? (
                    <p className="text-success mb-0">
                      <i className="fas fa-check-circle me-2"></i>
                      Sin multas pendientes
                    </p>
                  ) : (
                    <>
                      <p className="text-danger fw-bold mb-2">
                        Debe {formatCurrency(
                          multasSocio.filter(m => m.estado !== 'pagada').reduce((sum, m) => sum + (m.monto || 0), 0)
                        )} en multas pendientes:
                      </p>
                      <div className="mb-3">
                        {multasSocio.filter(m => m.estado !== 'pagada').map(m => (
                          <Form.Check
                            key={m._id}
                            type="checkbox"
                            id={`multa-pendiente-${m._id}`}
                            className="mb-1"
                            label={
                              <span>
                                <strong>{formatCurrency(m.monto)}</strong> — {m.motivo}{' '}
                                <small className="text-muted">({new Date(m.fecha).toLocaleDateString('es-ES')})</small>
                              </span>
                            }
                            checked={multasSeleccionadas.includes(m._id)}
                            onChange={() => toggleMultaSeleccionada(m._id)}
                          />
                        ))}
                      </div>
                      {hasPermission('socios', 'update') && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={pagarMultasSeleccionadas}
                          disabled={multasSeleccionadas.length === 0 || procesandoPagoMultas}
                        >
                          {procesandoPagoMultas ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <i className="fas fa-check me-1"></i>
                              Marcar {multasSeleccionadas.length > 0 ? `${multasSeleccionadas.length} ` : ''}como pagada{multasSeleccionadas.length !== 1 ? 's' : ''}
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-hand-holding-usd text-success me-2"></i>
                    Aportes ($)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="aportes"
                    value={formData.aportes}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-coins text-primary me-2"></i>
                    Capital Préstamo ($)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="capitalPrestamo"
                    value={formData.capitalPrestamo}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    disabled={!hasPrestamo}
                  />
                  <Form.Text className="text-muted">
                    {hasPrestamo ? 'Pago del principal' : 'No hay préstamo activo en este mes'}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-percent text-danger me-2"></i>
                    Interés Préstamo ($)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="interesPrestamo"
                    value={formData.interesPrestamo}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    disabled={!hasPrestamo}
                  />
                  <Form.Text className="text-muted">
                    {hasPrestamo ? 'Solo interés' : 'No hay préstamo activo en este mes'}
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-bus text-warning me-2"></i>
                    Paseo ($)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="paseo"
                    value={formData.paseo}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-star text-primary me-2"></i>
                    Programa ($)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="programa"
                    value={formData.programa}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </Form.Group>
              </Col>

            </Row>

            {/* Aportes atrasados: marcar qué meses cubre el aporte de este mes */}
            {!aporteExento && aportesPendientes.length > 0 && (() => {
              const aporteMes = parseFloat(formData.aportes) || 0;
              const paraEsteMes = Math.max(0, aporteMes - totalCubreAnteriores);
              const excede = totalCubreAnteriores > aporteMes + 0.001;
              const mesesMarcados = aportesPendientes.filter(p => cubreSeleccion[`${p.año}-${p.mes}`]);
              return (
                <Alert variant={excede ? 'danger' : 'warning'} className="mb-3">
                  <div className="fw-bold mb-2">
                    <i className="fas fa-calendar-times me-2"></i>
                    Este socio debe aporte de {aportesPendientes.length} mes(es) anterior(es). Marca cuáles cubre este pago:
                  </div>
                  <div className="d-flex flex-wrap gap-3 mb-2">
                    {aportesPendientes.map(p => {
                      const k = `${p.año}-${p.mes}`;
                      return (
                        <Form.Check
                          key={k}
                          type="checkbox"
                          id={`cubre-${k}`}
                          checked={!!cubreSeleccion[k]}
                          onChange={() => alternarMesCubierto(p)}
                          label={`${getNombreMes(p.mes)} ${p.año} (falta ${formatCurrency(p.falta)})`}
                        />
                      );
                    })}
                  </div>
                  <small className="d-block">
                    {mesesMarcados.length > 0 ? (
                      <>
                        De los <strong>{formatCurrency(aporteMes)}</strong> de aporte:{' '}
                        <strong>{formatCurrency(totalCubreAnteriores)}</strong> cubren{' '}
                        {mesesMarcados.map(p => `${getNombreMes(p.mes)} ${p.año}`).join(', ')}
                        {' '}y <strong>{formatCurrency(paraEsteMes)}</strong> quedan para {getNombreMes(formData.mes)} {formData.año}.
                      </>
                    ) : (
                      <>Sin marcar, todo el aporte cuenta para {getNombreMes(formData.mes)} {formData.año} y esos meses siguen pendientes.</>
                    )}
                    {excede && <strong className="d-block">Marcaste más de lo que aporta este mes.</strong>}
                    <span className="d-block text-muted mt-1">
                      <i className="fas fa-info-circle me-1"></i>
                      El dinero se registra como recibido en {getNombreMes(formData.mes)} {formData.año}; solo se marca qué meses atrasados quedan cubiertos.
                    </span>
                  </small>
                </Alert>
              );
            })()}

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-sticky-note me-2"></i>
                    Observaciones
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="observaciones"
                    value={formData.observaciones}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Notas adicionales..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="secondary">
              <Row>
                <Col>
                  <strong>Total del Mes:</strong>
                  <h4 className="text-primary">
                    {formatCurrency(
                      (typeof formData.aportes === 'number' ? formData.aportes : 0) +
                      (typeof formData.capitalPrestamo === 'number' ? formData.capitalPrestamo : 0) +
                      (typeof formData.interesPrestamo === 'number' ? formData.interesPrestamo : 0) +
                      (typeof formData.paseo === 'number' ? formData.paseo : 0) +
                      (typeof formData.programa === 'number' ? formData.programa : 0) +
                      (typeof formData.multas === 'number' ? formData.multas : 0)
                    )}
                  </h4>
                  <small className="text-muted">
                    (Aportes + Capital + Interés + Paseo + Programa + Multas)
                  </small>
                </Col>
              </Row>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="info" onClick={() => setShowCalculator(true)}>
              <i className="fas fa-calculator me-2"></i>
              Calculadora
            </Button>
            <Button variant="secondary" onClick={handleCloseModal}>
              <i className="fas fa-times me-2"></i>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              <i className="fas fa-save me-2"></i>
              Guardar Registro
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Calculadora */}
      <Calculator show={showCalculator} onHide={() => setShowCalculator(false)} />
    </Container>
  );
};

// Componente para mostrar préstamos activos
const PrestamosActivosInfo = ({ socioId, formatCurrency, calcularCuotaMensual, mesActual, añoActual, refreshTrigger }) => {
  const [prestamos, setPrestamos] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFinalizados, setShowFinalizados] = useState(false);

  useEffect(() => {
    fetchPrestamos();
    fetchRegistros();
  }, [socioId, mesActual, añoActual, refreshTrigger]);

  const fetchPrestamos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/solicitudes-prestamo?socio=${socioId}`);
      const prestamosFiltrados = (response.data.data || []).filter(p =>
        esPrestamoActivo(p.estado) || p.estado === 'finalizada'
      );
      // Ordenar: primero activas (aprobada/desembolsado), luego finalizadas; dentro de cada grupo por fecha desc
      prestamosFiltrados.sort((a, b) => {
        if (esPrestamoActivo(a.estado) && !esPrestamoActivo(b.estado)) return -1;
        if (!esPrestamoActivo(a.estado) && esPrestamoActivo(b.estado)) return 1;
        return new Date(b.fechaSolicitud) - new Date(a.fechaSolicitud);
      });
      setPrestamos(prestamosFiltrados);
    } catch (error) {
      console.error('Error al obtener préstamos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistros = async () => {
    try {
      const response = await axios.get(`/api/registros-mensuales?socio=${socioId}`);
      setRegistros(response.data.data || []);
    } catch (error) {
      console.error('Error al obtener registros:', error);
    }
  };


  if (loading) {
    return <Spinner animation="border" size="sm" />;
  }

  const prestamosActivos = prestamos.filter(p => esPrestamoActivo(p.estado));
  const prestamosFinalizados = prestamos.filter(p => p.estado === 'finalizada');
  const visiblePrestamos = showFinalizados ? prestamos : prestamosActivos;

  if (prestamos.length === 0) {
    return (
      <Alert variant="warning" className="mb-3">
        <i className="fas fa-info-circle me-2"></i>
        No hay préstamos activos para este socio
      </Alert>
    );
  }

  if (visiblePrestamos.length === 0 && prestamosFinalizados.length > 0) {
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 text-dark">
            <i className="fas fa-credit-card me-2"></i>
            Préstamos - {getNombreMes(mesActual)} {añoActual}
          </h6>
          <Form.Check
            type="switch"
            id="toggle-finalizados-empty"
            label={`Mostrar finalizados (${prestamosFinalizados.length})`}
            checked={showFinalizados}
            onChange={(e) => setShowFinalizados(e.target.checked)}
            className="text-dark"
          />
        </div>
        <Alert variant="success" className="mb-0 text-dark">
          <i className="fas fa-check-circle me-2"></i>
          No hay préstamos activos. Este socio tiene {prestamosFinalizados.length} préstamo(s) finalizado(s).
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 text-dark">
          <i className="fas fa-credit-card me-2"></i>
          Préstamos {showFinalizados ? '' : 'Activos'} - {getNombreMes(mesActual)} {añoActual}
        </h6>
        {prestamosFinalizados.length > 0 && (
          <Form.Check
            type="switch"
            id="toggle-finalizados"
            label={`Mostrar finalizados (${prestamosFinalizados.length})`}
            checked={showFinalizados}
            onChange={(e) => setShowFinalizados(e.target.checked)}
            className="text-dark"
          />
        )}
      </div>
      {visiblePrestamos.map((prestamo, index) => {
        const detalles = calcularCuotaMensual(prestamo);
        // Todo sale del mismo cronograma que la tabla de abajo
        const {
          mesesTranscurridos, mesesRestantes, cuotasPagadas, mesesSoloInteres,
          interesPagado, interesAcumulado, interesPendiente, totalPagado,
          capitalPendiente, deudaHoy, capitalMensual, interesFuturo, totalParaTerminar
        } = calcularPagosRealizados(prestamo, detalles, registros);

        const esFinalizado = prestamo.estado === 'finalizada';
        return (
          <Card key={prestamo._id} className={`mb-3 ${esFinalizado ? 'border-secondary' : 'border-info'}`}>
            <Card.Header className={esFinalizado ? 'bg-secondary text-white' : 'bg-primary text-white'}>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  Préstamo #{index + 1} - {prestamo.numeroSolicitud}
                </h6>
                {esFinalizado && (
                  <Badge bg="light" text="dark">
                    <i className="fas fa-flag-checkered me-1"></i>
                    FINALIZADO
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted">Monto Solicitado</small>
                    <h5 className="text-primary mb-0">
                      {formatCurrency(detalles?.monto || 0)}
                    </h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted">Plazo</small>
                    <h5 className="text-info mb-0">
                      {detalles?.plazo} meses
                    </h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted">Tasa de Interés</small>
                    <h5 className="text-warning mb-0">
                      {detalles?.tasaMensual}% mensual
                    </h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-2">
                    <small className="text-muted">Interés Total</small>
                    <h5 className="text-danger mb-0">
                      {formatCurrency(detalles?.interesTotal || 0)}
                    </h5>
                  </div>
                </Col>
              </Row>

              <hr className="my-3" />

              <Row>
                <Col md={4}>
                  <div className="p-3 bg-light rounded text-center">
                    <small className="text-muted d-block mb-2">Interés Mensual</small>
                    <h4 className="text-danger mb-0">
                      {formatCurrency(detalles?.interesAlMes || 0)}
                    </h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-light rounded text-center">
                    <small className="text-muted d-block mb-2">Capital Mensual</small>
                    <h4 className="text-primary mb-0">
                      {formatCurrency((detalles?.monto || 0) / (detalles?.plazo || 1))}
                    </h4>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="p-3 bg-success rounded text-center text-white">
                    <small className="d-block mb-2">Cuota Mensual Total</small>
                    <h4 className="mb-0">
                      {formatCurrency(detalles?.cuotaMensual || 0)}
                    </h4>
                  </div>
                </Col>
              </Row>

              <hr className="my-3" />

              <Row>
                <Col md={6}>
                  <Card className="bg-light border-success">
                    <Card.Body className="p-3">
                      <h6 className="text-success mb-3">
                        <i className="fas fa-check-circle me-2"></i>
                        Pagos Realizados
                      </h6>
                      <div className="mb-2">
                        <small className="text-muted">Cuotas Pagadas (con capital)</small>
                        <h5 className="text-success mb-0">{cuotasPagadas} de {detalles?.plazo}</h5>
                        {mesesSoloInteres > 0 && (
                          <small className="text-info">+ {mesesSoloInteres} mes(es) solo interés</small>
                        )}
                      </div>
                      <div>
                        <small className="text-muted">Total Pagado</small>
                        <h5 className="text-success mb-0">{formatCurrency(totalPagado)}</h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="bg-light border-danger">
                    <Card.Body className="p-3">
                      <h6 className="text-danger mb-3">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        Pendiente de Pago
                      </h6>
                      <Row className="g-2">
                        <Col xs={6}>
                          <div className="mb-2">
                            <small className="text-muted">Meses Restantes</small>
                            <h5 className="text-danger mb-0">{mesesRestantes} meses</h5>
                            {mesesRestantes > 0 && (
                              <small className="text-muted">a {formatCurrency(capitalMensual)} de capital por mes</small>
                            )}
                          </div>
                          <div>
                            <small className="text-muted">Capital Pendiente</small>
                            <h5 className="text-danger mb-0">{formatCurrency(capitalPendiente)}</h5>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="mb-2">
                            <small className="text-muted">Interés Vencido</small>
                            <h5 className="text-warning mb-0">{formatCurrency(interesPendiente)}</h5>
                          </div>
                          <div>
                            <small className="text-muted">Debe Hoy</small>
                            <h5 className="text-danger mb-0"><strong>{formatCurrency(deudaHoy)}</strong></h5>
                          </div>
                        </Col>
                      </Row>
                      {mesesRestantes > 0 && (
                        <small className="text-muted d-block mt-2">
                          Si paga puntual desde ahora, para terminar faltan <strong>{formatCurrency(totalParaTerminar)}</strong>
                          {' '}(incluye {formatCurrency(interesFuturo)} de interés de los {mesesRestantes} meses que faltan).
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <hr className="my-3" />

              <Row>
                <Col md={6}>
                  <Card className="bg-light border-warning">
                    <Card.Body className="p-3">
                      <h6 className="text-warning mb-3">
                        <i className="fas fa-hourglass-end me-2"></i>
                        Intereses Acumulados
                      </h6>
                      <div className="mb-2">
                        <small className="text-muted">Interés Generado hasta Hoy</small>
                        <h5 className="text-warning mb-0">{formatCurrency(interesAcumulado)}</h5>
                      </div>
                      <div>
                        <small className="text-muted">Interés Pagado</small>
                        <h5 className="text-success mb-0">{formatCurrency(interesPagado)}</h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="bg-light border-danger">
                    <Card.Body className="p-3">
                      <h6 className="text-danger mb-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Intereses Pendientes
                      </h6>
                      <div className="mb-2">
                        <small className="text-muted">Interés Vencido sin Pagar</small>
                        <h5 className="text-danger mb-0">{formatCurrency(interesPendiente)}</h5>
                      </div>
                      <div>
                        <small className="text-muted">Meses Transcurridos</small>
                        <h5 className="text-info mb-0">{mesesTranscurridos} meses</h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <hr className="my-3" />

              {/* Sección de Meses Impagos */}
              {(() => {
                const cronograma = generarCronogramaAmortizacion(prestamo, detalles, registros);
                const mesesImpagos = cronograma.filter(fila => fila.estado === 'Impago');
                
                if (mesesImpagos.length > 0) {
                  return (
                    <div className="mb-3">
                      <Card className="border-danger bg-light">
                        <Card.Header className="bg-danger text-white">
                          <h6 className="mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Detalles de Meses Impagos
                          </h6>
                        </Card.Header>
                        <Card.Body>
                          {mesesImpagos.map((mes, idx) => (
                            <div key={idx} className="mb-2 pb-2 border-bottom">
                              <strong>{mes.mes} {mes.año}</strong> (Vencimiento: {mes.fechaVencimiento})
                              <div className="text-danger mt-1">
                                <small>
                                  Capital: <strong>{formatCurrency(parseFloat(mes.cuotaMensual) - parseFloat(mes.interes))}</strong> + 
                                  Interés: <strong>{formatCurrency(parseFloat(mes.interes))}</strong> = 
                                  <strong> {formatCurrency(parseFloat(mes.cuotaMensual))}</strong>
                                </small>
                              </div>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </div>
                  );
                }
                return null;
              })()}

              <hr className="my-3" />

              {/* Tabla de Amortización */}
              <div className="mb-3">
                <h6 className="mb-3">
                  <i className="fas fa-table me-2"></i>
                  Cronograma de Amortización
                </h6>
                <div style={{ overflowX: 'auto' }}>
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead className="table-primary">
                      <tr>
                        <th>Período</th>
                        <th>Mes - Año</th>
                        <th>Vencimiento</th>
                        <th>Cuota Mensual</th>
                        <th>Interés</th>
                        <th>Estado</th>
                        <th>Capital Pagado</th>
                        <th>Interés Pagado</th>
                        <th>Saldo Restante</th>
                        <th>Saldo Total + Interés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generarCronogramaAmortizacion(prestamo, detalles, registros).map((fila, idx) => {
                        let rowClass = '';
                        if (fila.estado === 'Pagado') rowClass = 'table-success';
                        else if (fila.estado === 'Pagó Interés') rowClass = 'table-info';
                        else if (fila.estado === 'Impago') rowClass = 'table-danger';
                        else if (fila.estado === 'Pendiente') rowClass = 'table-warning';
                        
                        return (
                          <tr key={idx} className={rowClass}>
                            <td className="text-center"><strong>{fila.periodo}</strong></td>
                            <td>{fila.mes} {fila.año}</td>
                            <td>{fila.fechaVencimiento}</td>
                            <td className="text-right">{formatCurrency(parseFloat(fila.cuotaMensual))}</td>
                            <td className="text-right">{formatCurrency(parseFloat(fila.interes))}</td>
                            <td className="text-center">
                              <Badge bg={
                                fila.estado === 'Pagado' ? 'success' :
                                fila.estado === 'Pagó Interés' ? 'info' :
                                fila.estado === 'Impago' ? 'danger' :
                                fila.estado === 'Pendiente' ? 'warning' :
                                'secondary'
                              }>
                                {fila.estado === 'Pagado' && '✓ Pagado'}
                                {fila.estado === 'Pagó Interés' && '💰 Pagó Interés'}
                                {fila.estado === 'Impago' && '✗ Impago'}
                                {fila.estado === 'Pendiente' && '⏳ Pendiente'}
                                {fila.estado === 'No vencido' && '⏹️ No vencido'}
                              </Badge>
                            </td>
                            <td className="text-right">{formatCurrency(parseFloat(fila.capitalPagado))}</td>
                            <td className="text-right">{formatCurrency(parseFloat(fila.interesPagado))}</td>
                            <td className="text-right"><strong>{formatCurrency(parseFloat(fila.saldoRestante))}</strong></td>
                            <td className="text-right"><strong className="text-danger">{formatCurrency(parseFloat(fila.saldoTotalConInteres))}</strong></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Alert variant="light" className="mt-3 mb-0">
                <small>
                  <strong>Desglose:</strong> El socio debe pagar <strong>{formatCurrency(detalles?.cuotaMensual || 0)}</strong> mensuales, 
                  de los cuales <strong>{formatCurrency((detalles?.monto || 0) / (detalles?.plazo || 1))}</strong> son capital 
                  y <strong>{formatCurrency(detalles?.interesAlMes || 0)}</strong> son intereses. 
                  Ha pagado <strong>{cuotasPagadas}</strong> cuota(s) con capital
                  {mesesSoloInteres > 0 && <> y <strong>{mesesSoloInteres}</strong> mes(es) solo interés</>}.
                  Le quedan <strong>{formatCurrency(capitalPendiente)}</strong> de capital: <strong>{mesesRestantes}</strong> mes(es) más para terminar.
                  <br/><br/>
                  <strong>⚠️ Intereses:</strong> Desde el inicio del préstamo han corrido <strong>{mesesTranscurridos} meses</strong>,
                  que generan <strong>{formatCurrency(interesAcumulado)}</strong> de interés.
                  Se han pagado <strong>{formatCurrency(interesPagado)}</strong> y quedan vencidos <strong>{formatCurrency(interesPendiente)}</strong>.
                  Hoy debe <strong>{formatCurrency(deudaHoy)}</strong>.
                </small>
              </Alert>
            </Card.Body>
          </Card>
        );
      })}
    </div>
  );
};

export default ValoresManagement;
